/** Shared contract between the Express server and the React client. */

export type Unit =
  | 'g' | 'kg' | 'oz' | 'lb'
  | 'ml' | 'l' | 'tsp' | 'tbsp' | 'cup'
  | 'piece' | 'bunch' | 'can' | 'jar' | 'pack' | 'clove' | 'slice'
  | 'to taste'

export type Category =
  | 'produce' | 'protein' | 'dairy' | 'grain' | 'pantry'
  | 'spice' | 'condiment' | 'frozen' | 'bakery' | 'drink' | 'other'

export interface PantryItem {
  id: string
  name: string
  quantity: number | null
  unit: Unit | null
  category: Category
  /** ISO date (YYYY-MM-DD). Null when the item does not really expire. */
  expiresOn: string | null
  /** Staples you always have: flour, salt, olive oil. Never counted as "must use". */
  staple: boolean
  notes: string
  addedAt: string
}

export type EquipmentKind = 'appliance' | 'cookware' | 'bakeware' | 'tool'

export interface Equipment {
  id: string
  name: string
  kind: EquipmentKind
  /** Quirks that change how a recipe has to be written: "runs 20C hot", "3.5L basket". */
  notes: string
}

export type Skill = 'beginner' | 'intermediate' | 'advanced'

/** How willing the cook is to stop at a shop before cooking. */
export type Shopping = 'none' | 'quick' | 'happy'

export interface Preferences {
  diets: string[]
  allergies: string[]
  dislikes: string[]
  loves: string[]
  cuisines: string[]
  skill: Skill
  shopping: Shopping
  defaultServings: number
  defaultMaxMinutes: number
  spiceLevel: number
  notes: string
}

export type InceptionEffort = 'instant' | 'low' | 'medium' | 'high'

export const INCEPTION_EFFORTS: InceptionEffort[] = ['instant', 'low', 'medium', 'high']

export interface Settings {
  /** Perplexity cooks: stored context + your message -> recipes, in one call. */
  perplexityModel: string
  /** Inception reads prose into structured context. Fast matters more than deep here. */
  inceptionModel: string
  inceptionEffort: InceptionEffort
  recipeCount: number
  searchRecency: 'any' | 'year' | 'month' | 'week'
}

/* ---------- onboarding ---------- */

export type OnboardingStep = 'equipment' | 'limits' | 'taste' | 'shopping'

export const ONBOARDING_STEPS: OnboardingStep[] = ['equipment', 'limits', 'taste', 'shopping']

export interface Onboarding {
  /** Steps already answered, in order. Empty means a brand-new cook. */
  done: OnboardingStep[]
  completedAt: string | null
}

/* ---------- recipes ---------- */

export interface RecipeIngredient {
  item: string
  amount: string
  /** Only set when a pantry is tracked. */
  status?: 'have' | 'partial' | 'missing'
  note?: string
}

export interface Source {
  title: string
  url: string
}

export interface Recipe {
  id: string
  title: string
  blurb: string
  cuisine: string
  totalMinutes: number
  activeMinutes: number
  servings: number
  difficulty: Skill
  /** Badge-worthy attributes the composer assigns: vegetarian, spicy, one-pan… */
  tags: string[]
  ingredients: RecipeIngredient[]
  steps: string[]
  tips: string[]
  sources: Source[]
  /** Provisional at first, replaced by the source's own photo when one is found. */
  photo?: string
  /* Only when a pantry is tracked - otherwise nothing is known about what is in the house. */
  fitScore?: number
  fitReason?: string
  shoppingList?: string[]
  warnings?: string[]
}

export interface SavedRecipe {
  recipe: Recipe
  savedAt: string
  askedFor: string
}

/* ---------- chat ---------- */

export type ChatRole = 'user' | 'assistant'

export interface ChatMessage {
  id: string
  role: ChatRole
  text: string
  at: string
  /** Assistant turns that carry recipe cards. */
  recipes?: Recipe[]
  sources?: Source[]
  /** Set when the turn failed; `text` holds a readable explanation. */
  failed?: boolean
}

export interface AppState {
  onboarding: Onboarding
  equipment: Equipment[]
  preferences: Preferences
  pantry: PantryItem[]
  settings: Settings
  conversation: ChatMessage[]
  saved: SavedRecipe[]
}

/** Streamed newline-delimited JSON from POST /api/chat. */
export type ChatEvent =
  | { type: 'stage'; key: StageKey; status: 'running' | 'done' | 'error'; label: string; detail?: string }
  /** Pieces of the answer as they finish, before the turn is done. */
  | { type: 'partial'; reply?: string; recipes: Recipe[] }
  | { type: 'message'; message: ChatMessage }
  /** Photos found after the cards were sent; folds into the cards already on screen. */
  | { type: 'photos'; photos: Record<string, string> }
  | { type: 'error'; message: string }

export type StageKey = 'compose'

export interface HealthResponse {
  ok: boolean
  perplexity: boolean
  inception: boolean
  /** Settings keys pinned by .env. The UI shows these as fixed rather than editable. */
  envLocked: Array<keyof Settings>
}
